import { useCallback, useMemo, useRef, useState } from 'react';
import { buildAssignedOrderIdsByShiftTruck } from 'src/components/dispatch/newlyAssigned/buildAssignedOrderIdsByShiftTruck';
import { diffNewlyAssigned } from 'src/components/dispatch/newlyAssigned/diffNewlyAssigned';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

const EMPTY: ReadonlyMap<number, ReadonlySet<number>> = new Map();

/**
 * 自動配車で新しく割り付いた案件を車両ごとに持つ。
 *
 * 撮る (takeSnapshot) と有効化 (setEnabled) を分けているのは、/map の配車変更が
 * すべて controller を通るため。全モードで撮っておくと移動・入れ替えのあとに
 * スナップショットが最新へ更新され、古いハイライトが残らない。そのうえで
 * 有効化を自動配車・シナリオ配車に限れば、それらの操作では光らない。
 *
 * Why not 完了時に差分を確定して固定する: controller の onCompleted が返す
 * AutoDispatchResult は deliveries を持たない。完了時点では配車後の割当を
 * 取れないため、V3Plans と同じく「前は固定・後は現在値」で計算する。
 */
export function useNewlyAssignedHighlight(
  deliveries: readonly PlanningsDeliveryWithLegRunEntity[],
) {
  const [snapshot, setSnapshot] = useState<ReadonlyMap<number, ReadonlySet<number>> | null>(null);
  const [enabled, setEnabled] = useState(false);

  // takeSnapshot は controller の hooks から呼ばれるため引数を取れない。
  // 最新の deliveries をここで抱える
  const deliveriesRef = useRef(deliveries);
  deliveriesRef.current = deliveries;

  const takeSnapshot = useCallback(() => {
    setSnapshot(buildAssignedOrderIdsByShiftTruck(deliveriesRef.current));
    // 撮り直した時点で前回の差分は無効。有効化は完了時にやり直す
    setEnabled(false);
  }, []);

  const clear = useCallback(() => {
    setSnapshot(null);
    setEnabled(false);
  }, []);

  const newlyAssignedByShiftTruck = useMemo(() => {
    if (!enabled) return EMPTY;
    return diffNewlyAssigned(snapshot, buildAssignedOrderIdsByShiftTruck(deliveries));
  }, [enabled, snapshot, deliveries]);

  return { newlyAssignedByShiftTruck, takeSnapshot, setEnabled, clear };
}
